import {
    AppBarStringGroupIdentifier,
    AppBarStringResourceKeys,
} from '@office-start/appbar-control';
import { NovaLocalization, NovaLocalizer } from '@office-start/nova-facade-types';
import loc, { getPromiseForLocalizedString } from 'owa-localize';
import { LazyModule, LazyImport } from 'owa-bundling-light';

const LazyAppBarStringGroupIdentifierModule = new LazyModule(
    () => import('./strings/AppBarStrings.locstring.json')
);
const LazyAppBarStringGroupIdentifierStrings = new LazyImport(
    LazyAppBarStringGroupIdentifierModule,
    m => m.default
);

export const novaLocalization: NovaLocalization = {
    /**
     * Initializes a localizer for the specified string group.
     *
     * @template T
     * @param {string} stringGroup
     * @return {*}  {Promise<NovaLocalizer<T>>}
     */
    async initializeLocalizer<T extends string>(stringGroup: string): Promise<NovaLocalizer<T>> {
        switch (stringGroup) {
            case AppBarStringGroupIdentifier:
                // This typecast will fail the build if additional AppBarStringResourceKeys are added that are not in our locstrings
                return initializeStringGroup<AppBarStringResourceKeys>(
                    LazyAppBarStringGroupIdentifierStrings
                ) as Promise<NovaLocalizer<T>>;
            default:
                throw new Error(`Unrecognized string group: ${stringGroup}`);
        }
    },
};

async function initializeStringGroup<ExpectedKeys extends string>(
    stringImport: LazyImport<Record<ExpectedKeys, string>, any>
): Promise<NovaLocalizer<ExpectedKeys>> {
    const stringGroupMap = await stringImport.import();
    await Promise.all(
        Object.values(stringGroupMap).map(stringKey => getPromiseForLocalizedString(stringKey))
    );
    return {
        resolveString: (
            resourceKey: ExpectedKeys,
            _params?: {
                [paramKey: string]: string | number | Date | undefined;
            }
        ) => loc(stringGroupMap[resourceKey]),
    };
}
